/**
 * 노드 스테이킹 보상 계산 유틸리티 (v2.0)
 *
 * 특징:
 * - 초기 5개월 집중 모델: 85% 초기 지급
 * - 단계별 감소: 6~12개월 점진적 감소
 * - 랜덤 등락: 매일 다른 수익 (예측 불가)
 * - 정확한 32% APY: 1년 후 정확히 38,400 VTTN
 */

const DEFAULT_STAKING_AMOUNT = 120000;
const DEFAULT_APY = 32;
const TOTAL_DAYS = 365;

interface SegmentDefinition {
  length: number;
  startValue: number;
  endValue: number;
  slope: number;
  targetTotal: number;
}

const SEGMENT_DEFINITIONS: SegmentDefinition[] = [
  {
    length: 150,
    startValue: 218,
    endValue: 190,
    slope: 1.1,
    targetTotal: 218 * 150, // 32,700
  },
  {
    length: 60,
    startValue: 180,
    endValue: 80,
    slope: 1.2,
    targetTotal: 2350,
  },
  {
    length: 60,
    startValue: 80,
    endValue: 40,
    slope: 1.4,
    targetTotal: 1800,
  },
  {
    length: 60,
    startValue: 40,
    endValue: 20,
    slope: 1.6,
    targetTotal: 1200,
  },
  {
    length: 35,
    startValue: 20,
    endValue: 5,
    slope: 1.8,
    targetTotal: 350,
  },
];

const TWO_PI = Math.PI * 2;

interface DailyRewardParams {
  stakingAmount?: number;
  dayNumber: number;
}

interface AggregateRewardParams {
  stakingAmount?: number;
  daysElapsed: number;
}

interface ExpectedRewardParams {
  stakingAmount?: number;
  daysElapsed: number;
}

interface YearlyRewardsSimulationParams {
  stakingAmount?: number;
  baseAPY?: number;
}

interface DailyRewardPoint {
  day: number;
  reward: number;
  total: number;
}

interface WeeklyRewardPoint {
  week: number;
  reward: number;
}

interface MonthlyRewardPoint {
  month: number;
  reward: number;
}

interface YearlyRewardsSimulation {
  daily: DailyRewardPoint[];
  weekly: WeeklyRewardPoint[];
  monthly: MonthlyRewardPoint[];
  summary: {
    totalReward: number;
    avgDailyReward: number;
    firstDayReward: number;
    lastDayReward: number;
  };
}

/**
 * 일별 보상 계산 (초기 집중 + 단계별 감소 모델)
 *
 * @param params.stakingAmount - 스테이킹 금액 (기본 120,000 VTTN)
 * @param params.dayNumber - 경과 일수 (1부터 시작)
 * @returns 해당 일의 보상 (VTTN)
 */
export function calculateDailyReward({
  stakingAmount = DEFAULT_STAKING_AMOUNT,
  dayNumber,
}: DailyRewardParams): number {
  const reward = computeDailyReward({ stakingAmount, dayNumber });
  return parseFloat(reward.toFixed(2));
}

/**
 * 누적 보상 계산 (1일부터 현재까지)
 *
 * @param params.stakingAmount - 스테이킹 금액
 * @param params.daysElapsed - 경과 일수
 * @returns 누적 보상
 */
export function calculateTotalRewards({
  stakingAmount = DEFAULT_STAKING_AMOUNT,
  daysElapsed,
}: AggregateRewardParams): number {
  let totalRewards = 0;

  for (let day = 1; day <= daysElapsed; day += 1) {
    totalRewards += computeDailyReward({ stakingAmount, dayNumber: day });
  }

  return parseFloat(totalRewards.toFixed(2));
}

/**
 * 예상 남은 보상 계산 (현재부터 365일까지)
 *
 * @param params.stakingAmount - 스테이킹 금액
 * @param params.daysElapsed - 경과 일수
 * @returns 예상 남은 보상
 */
export function calculateExpectedRemainingRewards({
  stakingAmount = DEFAULT_STAKING_AMOUNT,
  daysElapsed,
}: ExpectedRewardParams): number {
  let expectedRemaining = 0;

  for (let day = daysElapsed + 1; day <= TOTAL_DAYS; day += 1) {
    expectedRemaining += computeDailyReward({ stakingAmount, dayNumber: day });
  }

  return parseFloat(expectedRemaining.toFixed(2));
}

/**
 * 1년간 전체 보상 시뮬레이션 (그래프용)
 *
 * @param params.stakingAmount - 스테이킹 금액
 * @param params.baseAPY - 연 수익률 (사용 안 함, 호환성 유지)
 * @returns 일별/주별/월별 데이터 + 요약
 */
export function simulateYearlyRewards({
  stakingAmount = DEFAULT_STAKING_AMOUNT,
  baseAPY = DEFAULT_APY,
}: YearlyRewardsSimulationParams = {}): YearlyRewardsSimulation {
  const dailyData: DailyRewardPoint[] = [];
  const weeklyData: WeeklyRewardPoint[] = [];
  const monthlyData: MonthlyRewardPoint[] = [];

  let totalRewards = 0;
  let weeklyReward = 0;
  let monthlyReward = 0;

  for (let day = 1; day <= TOTAL_DAYS; day += 1) {
    const dailyReward = computeDailyReward({ stakingAmount, dayNumber: day, baseAPY });
    totalRewards += dailyReward;
    weeklyReward += dailyReward;
    monthlyReward += dailyReward;

    dailyData.push({
      day,
      reward: parseFloat(dailyReward.toFixed(2)),
      total: parseFloat(totalRewards.toFixed(2)),
    });

    if (day % 7 === 0) {
      weeklyData.push({
        week: Math.floor(day / 7),
        reward: parseFloat(weeklyReward.toFixed(2)),
      });
      weeklyReward = 0;
    }

    if (day % 30 === 0) {
      monthlyData.push({
        month: Math.floor(day / 30),
        reward: parseFloat(monthlyReward.toFixed(2)),
      });
      monthlyReward = 0;
    }
  }

  return {
    daily: dailyData,
    weekly: weeklyData,
    monthly: monthlyData,
    summary: {
      totalReward: parseFloat(totalRewards.toFixed(2)),
      avgDailyReward: parseFloat((totalRewards / TOTAL_DAYS).toFixed(2)),
      firstDayReward: dailyData[0]?.reward ?? 0,
      lastDayReward: dailyData[TOTAL_DAYS - 1]?.reward ?? 0,
    },
  };
}

/**
 * 일별 배율 계산 (하위 호환성 유지)
 * @deprecated - calculateDailyReward 사용 권장
 */
export function calculateDailyMultiplier(dayNumber: number, baseAPY: number = DEFAULT_APY): number {
  const baseDailyReward = (DEFAULT_STAKING_AMOUNT * baseAPY) / TOTAL_DAYS / 100;
  const reward = calculateDailyReward({ stakingAmount: DEFAULT_STAKING_AMOUNT, dayNumber });
  return parseFloat((reward / baseDailyReward).toFixed(4));
}

function getDeterministicNoise(dayNumber: number, seed: number = 0): number {
  const raw = Math.sin(dayNumber * 12.9898 + seed * 78.233) * 43758.5453;
  const fractional = raw - Math.floor(raw);
  return (fractional - 0.5) * 0.04; // ±2%
}

function getCycleOffset(dayNumber: number): number {
  return Math.sin(((dayNumber - 1) / 30) * TWO_PI) * 0.05; // ±5%
}

function getDailyRewardSchedule(stakingAmount: number, baseAPY: number = DEFAULT_APY): number[] {
  const scaleFactor =
    (stakingAmount * baseAPY) / (DEFAULT_STAKING_AMOUNT * DEFAULT_APY);

  const scaledBase = defaultBaseSchedule.map((value) => value * scaleFactor);

  const varied = scaledBase.map((value, index) => {
    const dayNumber = index + 1;
    const cycleOffset = getCycleOffset(dayNumber);
    const noise = getDeterministicNoise(dayNumber, stakingAmount);
    const multiplier = 1 + cycleOffset + noise;
    return Math.max(value * 0.4, value * multiplier);
  });

  normalizeSegments(varied, scaleFactor);

  return varied.map((value) => parseFloat(value.toFixed(6)));
}

function getDailyRewardForDay(dayNumber: number, stakingAmount: number, baseAPY: number = DEFAULT_APY): number {
  const boundedDay = Math.min(Math.max(dayNumber, 1), TOTAL_DAYS);
  const cacheKey = `${stakingAmount}-${baseAPY}`;
  if (!cachedDailyRewards.has(cacheKey)) {
    cachedDailyRewards.set(cacheKey, getDailyRewardSchedule(stakingAmount, baseAPY));
  }
  const rewards = cachedDailyRewards.get(cacheKey)!;
  return rewards[boundedDay - 1];
}

function computeDailyReward({
  stakingAmount = DEFAULT_STAKING_AMOUNT,
  dayNumber,
  baseAPY = DEFAULT_APY,
}: DailyRewardParams & { baseAPY?: number }): number {
  return getDailyRewardForDay(dayNumber, stakingAmount, baseAPY);
}

const defaultBaseSchedule = buildDefaultBaseSchedule();
const cachedDailyRewards: Map<string, number[]> = new Map();

function buildDefaultBaseSchedule(): number[] {
  const schedule: number[] = [];

  SEGMENT_DEFINITIONS.forEach((segment) => {
    const rawValues: number[] = [];
    const denominator = Math.max(segment.length - 1, 1);

    for (let i = 0; i < segment.length; i += 1) {
      const progress = i / denominator;
      const decay = Math.pow(progress, segment.slope);
      const value =
        segment.startValue - (segment.startValue - segment.endValue) * decay;
      rawValues.push(value);
    }

    const firstValue = rawValues[0];
    const rawSum = rawValues.reduce((sum, value) => sum + value, 0);
    const remainingRawSum = rawSum - firstValue;
    const targetRemaining = segment.targetTotal - firstValue;

    let factor = 1;
    if (segment.length > 1 && remainingRawSum > 0) {
      factor = targetRemaining / remainingRawSum;
    }

    rawValues.forEach((value, index) => {
      if (index === 0) {
        schedule.push(firstValue);
      } else {
        schedule.push(value * factor);
      }
    });
  });

  return schedule;
}

function normalizeSegments(values: number[], scaleFactor: number): void {
  let cursor = 0;

  SEGMENT_DEFINITIONS.forEach((segment) => {
    const end = cursor + segment.length;
    const targetTotal = segment.targetTotal * scaleFactor;
    let actualTotal = 0;

    for (let i = cursor; i < end; i += 1) {
      actualTotal += values[i];
    }

    const factor = actualTotal === 0 ? 1 : targetTotal / actualTotal;

    for (let i = cursor; i < end; i += 1) {
      values[i] = values[i] * factor;
    }

    cursor = end;
  });
}

